import { createContext, useState, useEffect } from "react";
import axiosInstance from "../utils/AxiosConfig";
import { setLogoutFunction } from "../context/AuthHelper";
import PlatformBridge from "../utils/PlatformBridge";

const AuthContext = createContext();

const refreshStoredPermissions = async () => {
  const token = await PlatformBridge.getToken("accessToken");
  if (!token) return;

  try {
    const response = await axiosInstance.get("/staff/profile");
    const permissions = response?.data?.data?.permissions;
    if (Array.isArray(permissions)) {
      localStorage.setItem("permissions", JSON.stringify(permissions));
    }
  } catch (error) {
    console.error("Error refreshing stored permissions:", error);
  }
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [role, setRole] = useState(null);
  // Se pone en true recién cuando terminó de intentar restaurar la sesión
  // desde el storage seguro — hasta entonces, PrivateRoute/PublicRoute no
  // deben decidir la ruta (evita un flash-redirect a /login en cada arranque).
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const storedAccessToken = await PlatformBridge.getToken("accessToken");
        const storedRefreshToken = await PlatformBridge.getToken("refreshToken");
        const storedRole = localStorage.getItem("role");
        if (!active) return;

        // Necesitamos accessToken, refreshToken y role
        if (storedAccessToken && storedRole && storedRefreshToken && storedRefreshToken !== "undefined") {
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setRole(storedRole);
        }
      } catch (error) {
        console.error("Error al cargar datos desde el storage seguro:", error);
      } finally {
        if (active) setAuthReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Sincronizar estado cuando AxiosConfig renueva el token (para que el resto de la app vea el nuevo token)
  useEffect(() => {
    const onTokenRefreshed = (event) => {
      const newToken = event.detail;
      if (newToken) {
        setAccessToken(newToken);
        refreshStoredPermissions();
      }
    };
    window.addEventListener("tokenRefreshed", onTokenRefreshed);
    return () => window.removeEventListener("tokenRefreshed", onTokenRefreshed);
  }, []);

  useEffect(() => {
    if (accessToken) {
      refreshStoredPermissions();
    }
  }, [accessToken]);

  const login = async (email, password) => {
    try {
      const userAgent = PlatformBridge.isDesktop() ? "DESKTOP" : "WEB";
      console.log("🔐 [AuthContext] Login request", {
        email,
        userAgent,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL || "http://localhost:8080",
      });
      const response = await axiosInstance.post("/session/login", {
        email,
        password,
        user_agent: userAgent,
      });

      console.log("✅ [AuthContext] Login response", {
        status: response.status,
        data: response.data,
      });

      const { accessToken, refreshToken, role, permissions, empresaId, isSuperAdmin, companyType } = response.data.data;

      // Extraer el dominio del email del usuario
      const emailDomain = email.includes('@') ? `@${email.split('@')[1]}` : '@miempresa.com';

      // Persistir todo ANTES de tocar el estado de React: setAccessToken dispara
      // el redirect de PublicRoute en cuanto React re-renderiza, y si eso pasa
      // antes de que role/permissions estén en localStorage, un reload
      // inmediato (como hace un test e2e que navega apenas cambia la URL) restaura
      // la sesión sin role y se pierde el login. Con los await de PlatformBridge
      // en el medio, ese hueco existe donde antes (todo síncrono) no existía.
      const hasRefreshToken = refreshToken && refreshToken !== 'undefined';
      if (hasRefreshToken) {
        await PlatformBridge.setToken("refreshToken", refreshToken);
      } else {
        await PlatformBridge.removeToken("refreshToken");
      }
      await PlatformBridge.setToken("accessToken", accessToken);
      PlatformBridge.notifyLogin();

      localStorage.setItem("userEmail", email);
      // Solo guardar empresaId si existe (los usuarios core no tienen empresaId)
      if (empresaId) {
        localStorage.setItem("empresaId", empresaId.toString());
      } else {
        localStorage.removeItem("empresaId");
      }
      localStorage.setItem("role", role || '');
      localStorage.setItem("permissions", JSON.stringify(permissions || []));
      localStorage.setItem("emailDomain", emailDomain);
      localStorage.setItem("companyType", companyType);
      localStorage.setItem("isSuperAdmin", (isSuperAdmin === true).toString());

      setRefreshToken(hasRefreshToken ? refreshToken : null);
      setRole(role);
      setAccessToken(accessToken);

      // El proveedor de WebSocket puede haberse montado antes del login.
      // Avisarle ahora que ya cuenta con token y tenant para abrir la conexión.
      window.dispatchEvent(new CustomEvent("tokenRefreshed", { detail: accessToken }));

      return { status_code: 200 };
    } catch (error) {
      console.error("❌ [AuthContext] Login error", {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        baseURL: error?.config?.baseURL,
      });
      
      // Manejar errores específicos
      // Devolver la clave del mensaje para que el componente Login pueda traducirlo
      let errorMessage = "Error desconocido";
      
      if (error?.response?.status === 401) {
        // El login no expone la causa puntual (usuario, contraseña o estado)
        // y la UI siempre debe mostrar el mensaje localizado de credenciales.
        errorMessage = "invalid_credentials";
      } else if (error?.response?.data?.error_message) {
        // Si el backend devuelve una clave de mensaje, devolverla tal cual para traducirla
        errorMessage = error.response.data.error_message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 404) {
        // Si es 404 sin mensaje específico, usar la clave de traducción
        errorMessage = "empresa_not_found";
      }
      
      return {
        status_code: error?.response?.status || 500,
        error_message: errorMessage,
      };
    }
  };

  const logout = async () => {
    try {
      const storedEmpresaId = localStorage.getItem("empresaId");

      // Solo intentar logout en el backend si tenemos empresaId
      if (storedEmpresaId) {
        await axiosInstance.post("/session/logout", {
          accessToken: accessToken,
          refreshToken: refreshToken,
          empresaId: parseInt(storedEmpresaId)
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    }

    setAccessToken(null);
    setRefreshToken(null);
    setRole(null);

    // Los tokens ya no viven en localStorage, así que localStorage.clear() no
    // los va a tocar — hay que borrarlos explícitamente del storage seguro.
    await PlatformBridge.removeToken("accessToken");
    await PlatformBridge.removeToken("refreshToken");

    // Guardar el terminalId antes de limpiar localStorage
    const terminalId = localStorage.getItem("terminalId");

    // Limpiar localStorage
    localStorage.clear();

    // Restaurar el terminalId después de limpiar
    if (terminalId) {
      localStorage.setItem("terminalId", terminalId);
      console.log("✅ TerminalId preservado durante logout:", terminalId);
    }
  };

  // 👇 registrar la función logout globalmente
  useEffect(() => {
    setLogoutFunction(logout);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ accessToken, refreshToken, role, authReady, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
